import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../tenant";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Clock, Lock, FileCheck, Building2, CheckCircle2, QrCode, Fingerprint, BarChart3, Send } from "lucide-react";

export default function LoginPage() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const tenant = useTenant();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(tenant || null);
  const [mode, setMode] = useState("login");
  const [forgotSent, setForgotSent] = useState(false);
  const [demo, setDemo] = useState({ name: "", email: "", organization: "", message: "" });
  const [demoSent, setDemoSent] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submitDemo = async (e) => {
    e.preventDefault();
    setDemoLoading(true);
    try {
      await api.post("/demo-requests", demo);
      setDemoSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setDemoLoading(false);
    }
  };

  useEffect(() => {
    if (tenant) return;
    const slug = new URLSearchParams(window.location.search).get("portal") || "";
    api.get("/branding", { params: { host: window.location.hostname, slug } })
      .then((r) => { if (r.data?.found) setBrand(r.data); })
      .catch(() => {});
  }, [tenant]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (brand?.client_id && user.client_id && user.client_id !== brand.client_id) {
        await logout();
        setError(`This account doesn't belong to the ${brand.name} portal.`);
        return;
      }
      navigate(user.role === "provider" ? "/scan" : "/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hero-canvas relative min-h-screen overflow-hidden text-white">
      <div className="hero-grid pointer-events-none absolute inset-0" />

      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 lg:px-10">
        <div className="flex items-center gap-3">
          {brand?.logo_url ? (
            <>
              <img src={brand.logo_url} alt={brand.name} className="h-10 w-auto max-w-[170px] rounded-xl bg-white object-contain p-1" data-testid="login-client-logo" />
              <div>
                <div className="font-['Inter'] text-xl font-bold tracking-tight" data-testid="login-brand-name">{brand.name}</div>
                <div className="text-[9px] font-bold uppercase tracking-[0.28em] text-teal-100/50">Powered by TAGVerify</div>
              </div>
            </>
          ) : brand ? (
            <div>
              <div className="font-['Inter'] text-xl font-bold tracking-tight" data-testid="login-brand-name">{brand.name}</div>
              <div className="text-[9px] font-bold uppercase tracking-[0.28em] text-teal-100/50">Powered by TAGVerify</div>
            </div>
          ) : (
            <div className="rounded-2xl bg-white px-4 py-2 shadow-lg" data-testid="login-brand-name">
              <img src="/tagverify-logo.png" alt="TAGVerify" className="h-9 w-auto object-contain" />
            </div>
          )}
        </div>
        <div className="hidden items-center gap-6 md:flex">
          {!brand && (
            <nav className="flex items-center gap-6 text-sm font-medium text-teal-100/70">
              <a href="#how" className="transition-colors duration-200 hover:text-white" data-testid="nav-how-it-works">How it works</a>
              <a href="#pricing" className="transition-colors duration-200 hover:text-white" data-testid="nav-pricing">Pricing</a>
              <a href="#demo" className="rounded-full bg-[#C7A34A] px-4 py-1.5 font-semibold text-slate-900 transition-colors duration-200 hover:bg-[#d4b25e]" data-testid="nav-request-demo">Request a demo</a>
            </nav>
          )}
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-teal-100/70">
            <Lock size={12} className="text-[#C7A34A]" /> Enterprise-grade
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-6 pb-16 pt-4 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:pt-8">
        <div className="fade-up">
          <div className="mb-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C7A34A]">
            <span className="h-px w-8 bg-[#C7A34A]/60" /> Built for schools & districts
          </div>
          <h1 className="font-['Inter'] text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
            Every pickup.<br />Every return.<br />
            <span className="text-[#C7A34A]">Verified in one tap.</span>
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-teal-100/70">
            The multi-tenant accountability platform for related-service delivery — live dashboards,
            QR verification and audit-ready reporting for districts, charters and staffing agencies.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {[
              { icon: FileCheck, label: "Audit-ready exports" },
              { icon: Building2, label: "Multi-tenant isolation" },
              { icon: Clock, label: "Real-time return alerts" },
            ].map((f) => (
              <span key={f.label} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-teal-50/80">
                <f.icon size={13} className="text-teal-300" /> {f.label}
              </span>
            ))}
          </div>

          <div className="relative mt-14 hidden h-72 lg:block" aria-hidden="true">
            <div className="glass-card float-a absolute left-0 top-2 w-[340px] rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold text-teal-50/90">
                  <span className="pulse-dot h-2 w-2 rounded-full bg-emerald-400" /> Currently In Service
                </div>
                <span className="rounded-full bg-white/10 px-2.5 py-0.5 font-mono text-[10px] text-teal-100/70">LIVE</span>
              </div>
              {[
                { init: "JS", name: "John S.", svc: "Speech Therapy", t: "24m" },
                { init: "MB", name: "Maya B.", svc: "Counseling", t: "08m" },
              ].map((r) => (
                <div key={r.init} className="mt-3 flex items-center justify-between rounded-xl bg-white/5 px-3 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#C7A34A]/20 text-[11px] font-bold text-[#C7A34A]">{r.init}</span>
                    <div>
                      <div className="text-sm font-semibold text-white">{r.name}</div>
                      <div className="text-[11px] text-teal-100/60">{r.svc}</div>
                    </div>
                  </div>
                  <span className="font-mono text-xs text-emerald-300">{r.t}</span>
                </div>
              ))}
            </div>
            <div className="glass-card float-b absolute left-[300px] top-36 w-[290px] rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-400/15">
                  <CheckCircle2 size={20} className="text-emerald-300" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-white">Maya B. returned</div>
                  <div className="text-[11px] text-teal-100/60">Counseling · 32 min · Verified 2:41 PM</div>
                </div>
              </div>
            </div>
            <div className="glass-card absolute left-56 -top-7 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-teal-50/90">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C7A34A]" /> 12 verifications today
            </div>
          </div>
        </div>

        <div className="w-full max-w-md justify-self-center lg:justify-self-end">
          <div className="rounded-3xl bg-white p-8 text-slate-900 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
            <h2 className="font-['Inter'] text-2xl font-bold tracking-tight text-slate-900">
              {mode === "login" ? "Sign in" : "Reset password"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {mode === "login" ? (brand ? `Access the ${brand.name} portal` : "Access your secure portal") : "We'll email you a reset link"}
            </p>
            {mode === "forgot" ? (
              <form onSubmit={submitForgot} className="mt-6 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@organization.edu" data-testid="forgot-email-input" className="h-12 rounded-xl" />
                </div>
                {forgotSent && (
                  <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700" data-testid="forgot-success">
                    If an account exists for that email, a password reset link has been sent.
                  </div>
                )}
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
                )}
                <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                  style={brand?.primary_color ? { backgroundColor: brand.primary_color } : undefined}
                  className="h-12 w-full rounded-full bg-primary text-base font-semibold hover:bg-primary/90">
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
                <button type="button" onClick={() => { setMode("login"); setForgotSent(false); setError(""); }}
                  data-testid="back-to-login-link"
                  className="w-full text-center text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-slate-900">
                  ← Back to sign in
                </button>
              </form>
            ) : (
            <form onSubmit={submit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@organization.edu" data-testid="login-email-input" className="h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" data-testid="login-password-input" className="h-12 rounded-xl" />
              </div>
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                style={brand?.primary_color ? { backgroundColor: brand.primary_color } : undefined}
                className="h-12 w-full rounded-full bg-primary text-base font-semibold hover:bg-primary/90">
                {loading ? "Signing in..." : "Sign in"}
              </Button>
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link"
                className="w-full text-center text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-slate-900">
                Forgot password?
              </button>
            </form>
            )}
          </div>
          <p className="mt-5 text-center text-xs text-teal-100/40">A product of The Allicock Group · TAG ecosystem</p>
        </div>
      </main>

      {!brand && (
        <>
          <section id="how" className="relative z-10 mx-auto max-w-7xl px-6 py-20 lg:px-10">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C7A34A]">How it works</div>
            <h2 className="max-w-xl font-['Inter'] text-3xl font-extrabold tracking-tight sm:text-4xl">
              Verification in three effortless steps
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { icon: QrCode, step: "01", title: "Scan the badge", body: "Providers scan a student's printed QR badge — or search by name — from any phone. No app install, no training." },
                { icon: Fingerprint, step: "02", title: "One tap to verify", body: "PICK UP when service starts, RETURN when it ends. Date, time, provider, school and service are stamped automatically." },
                { icon: BarChart3, step: "03", title: "Everything reported", body: "Live dashboards, missing-return alerts and audit-ready daily, provider and district reports in CSV, Excel or PDF." },
              ].map((s) => (
                <div key={s.step} className="glass-card card-lift rounded-2xl p-7" data-testid={`how-step-${s.step}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#C7A34A]/15 text-[#C7A34A]"><s.icon size={20} /></span>
                    <span className="font-mono text-sm font-bold text-white/25">{s.step}</span>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-teal-100/60">{s.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="pricing" className="relative z-10 mx-auto max-w-7xl px-6 py-20 lg:px-10">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C7A34A]">Pricing</div>
            <h2 className="max-w-xl font-['Inter'] text-3xl font-extrabold tracking-tight sm:text-4xl">
              Simple plans that scale with your district
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { name: "Starter", price: "$99", per: "/school/mo", desc: "For a single school or program", features: ["Unlimited students & providers", "QR badges & sticker printing", "Live dashboard & alerts", "CSV / Excel / PDF reports"], popular: false },
                { name: "District", price: "$79", per: "/school/mo", desc: "For districts & networks (5+ schools)", features: ["Everything in Starter", "Multi-school rollup reporting", "Bulk CSV roster imports", "Priority support"], popular: true },
                { name: "Enterprise", price: "Custom", per: "", desc: "For agencies & white-label partners", features: ["White-label branding & subdomain", "Custom integrations (Clever, PowerSchool)", "SLA & dedicated onboarding", "Volume licensing"], popular: false },
              ].map((p) => (
                <div key={p.name} className={`glass-card card-lift relative rounded-2xl p-7 ${p.popular ? "border-[#C7A34A]/50" : ""}`} data-testid={`pricing-${p.name.toLowerCase()}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-6 rounded-full bg-[#C7A34A] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-900">Most popular</span>
                  )}
                  <h3 className="text-lg font-bold text-white">{p.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-['Inter'] text-4xl font-extrabold tracking-tight text-white">{p.price}</span>
                    <span className="text-sm text-teal-100/50">{p.per}</span>
                  </div>
                  <p className="mt-1 text-sm text-teal-100/60">{p.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-teal-50/80">
                        <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-teal-300" /> {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#demo" className={`mt-7 block rounded-full py-2.5 text-center text-sm font-semibold transition-colors duration-200 ${
                    p.popular ? "bg-[#C7A34A] text-slate-900 hover:bg-[#d4b25e]" : "border border-white/15 text-white hover:bg-white/10"
                  }`}>
                    {p.price === "Custom" ? "Talk to sales" : "Request a demo"}
                  </a>
                </div>
              ))}
            </div>
          </section>

          <section id="demo" className="relative z-10 mx-auto max-w-7xl px-6 py-20 lg:px-10">
            <div className="glass-card grid gap-10 rounded-3xl p-8 md:p-12 lg:grid-cols-2">
              <div>
                <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C7A34A]">Request a demo</div>
                <h2 className="font-['Inter'] text-3xl font-extrabold tracking-tight sm:text-4xl">
                  See TAGVerify on your district's data
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-teal-100/60">
                  A 20-minute walkthrough with our team — live scanning, dashboards and reporting,
                  plus white-label options for agencies and charter networks.
                </p>
                <div className="mt-6 space-y-3 text-sm text-teal-50/80">
                  <div className="flex items-center gap-2"><CheckCircle2 size={15} className="text-teal-300" /> No commitment, no setup required</div>
                  <div className="flex items-center gap-2"><CheckCircle2 size={15} className="text-teal-300" /> Bring your own roster — we'll import it live</div>
                  <div className="flex items-center gap-2"><CheckCircle2 size={15} className="text-teal-300" /> Response within one business day</div>
                </div>
              </div>
              {demoSent ? (
                <div className="flex flex-col items-center justify-center text-center" data-testid="demo-success">
                  <div className="success-pop flex h-20 w-20 items-center justify-center rounded-full bg-emerald-400/15">
                    <CheckCircle2 size={40} className="text-emerald-300" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-white">Request received</h3>
                  <p className="mt-2 max-w-xs text-sm text-teal-100/60">Thanks {demo.name.split(" ")[0]} — our team will reach out within one business day.</p>
                </div>
              ) : (
                <form onSubmit={submitDemo} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input required value={demo.name} onChange={(e) => setDemo({ ...demo, name: e.target.value })}
                      placeholder="Full name" data-testid="demo-name-input"
                      className="h-12 rounded-xl border border-white/15 bg-white/5 px-4 text-sm text-white placeholder-teal-100/40 focus:outline-none focus:ring-2 focus:ring-[#C7A34A]/60" />
                    <input required type="email" value={demo.email} onChange={(e) => setDemo({ ...demo, email: e.target.value })}
                      placeholder="Work email" data-testid="demo-email-input"
                      className="h-12 rounded-xl border border-white/15 bg-white/5 px-4 text-sm text-white placeholder-teal-100/40 focus:outline-none focus:ring-2 focus:ring-[#C7A34A]/60" />
                  </div>
                  <input required value={demo.organization} onChange={(e) => setDemo({ ...demo, organization: e.target.value })}
                    placeholder="District / organization" data-testid="demo-org-input"
                    className="h-12 w-full rounded-xl border border-white/15 bg-white/5 px-4 text-sm text-white placeholder-teal-100/40 focus:outline-none focus:ring-2 focus:ring-[#C7A34A]/60" />
                  <textarea rows={4} value={demo.message} onChange={(e) => setDemo({ ...demo, message: e.target.value })}
                    placeholder="Tell us about your schools, providers and timeline (optional)" data-testid="demo-message-input"
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder-teal-100/40 focus:outline-none focus:ring-2 focus:ring-[#C7A34A]/60" />
                  <button type="submit" disabled={demoLoading} data-testid="demo-submit-button"
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#C7A34A] text-sm font-bold text-slate-900 transition-colors duration-200 hover:bg-[#d4b25e] disabled:opacity-50">
                    <Send size={15} /> {demoLoading ? "Sending..." : "Request demo"}
                  </button>
                </form>
              )}
            </div>
          </section>

          <footer className="relative z-10 border-t border-white/5">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-8 text-xs text-teal-100/40 lg:px-10">
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} /> TAGVerify — Service Verification Platform
              </div>
              <div>© {new Date().getFullYear()} The Allicock Group. All rights reserved.</div>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}
